import { useCallback, useMemo, useState, useEffect } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import {
  Button,
  Text,
  ControllerModal,
  ControllerValidatedTextField,
  DialogActions,
  DialogContent,
  Stack,
  useForm,
  valSchema,
  valString,
  yupResolver,
  CircularProgress,
  ControllerAlert as Alert,
  OutlinedInput,
  IconButton,
  MdCopyAll,
  Tooltip,
  ClickAwayListener,
} from '@bymason-private/legacy-pattern-components';
import { useGenerateDeviceReport, GenerateDeviceReportResult } from '@bymason/controller-api-hooks';

const schema = valSchema().shape({
  email: valString().email().required(),
});

interface GenerateDeviceReportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (result: GenerateDeviceReportResult) => void;
  onError?: (message: string, code?: number) => void;
}

export function GenerateDeviceReportModal({
  onSuccess,
  onError,
  onClose,
  open,
}: GenerateDeviceReportModalProps) {
  const { authToken = '', customerId = '', userInfo } = useAuth();
  const [
    genReport,
    reportResult,
    reportError,
    reportBusy,
    reportErrorCode,
    ,
    clearReport,
  ] = useGenerateDeviceReport(authToken);
  const [showCopied, setShowCopied] = useState(false);

  const {
    control,
    formState: { isValid },
    getValues,
    reset,
    trigger,
  } = useForm<{ email: string }>({
    mode: 'onChange',
    defaultValues: {
      email: userInfo?.email || '',
    },
    resolver: yupResolver(schema),
  });

  const handleClose = () => {
    onClose();
    clearReport();
    reset();
  };

  const handleSubmit = () => {
    trigger();

    if (isValid) {
      const vals = getValues();

      genReport({ customerId, email: encodeURIComponent(vals.email) });
    }
  };

  const handleCopyToClipboard = useCallback(() => {
    reportResult && navigator.clipboard.writeText(reportResult.uuid);
    setShowCopied(true);
  }, [reportResult]);

  const handleTooltipClose = () => {
    setShowCopied(false);
  };

  const additionalTooltipProps = useMemo(
    () =>
      showCopied
        ? {
            title: 'Copied!',
            onClose: handleTooltipClose,
            open: showCopied,
          }
        : {},
    [showCopied]
  );

  useEffect(() => {
    if (!reportBusy && reportResult && onSuccess) {
      onSuccess(reportResult);
    }
  }, [onSuccess, reportBusy, reportResult]);

  useEffect(() => {
    if (reportError && onError) {
      onError(reportError, reportErrorCode);
    }
  }, [onError, reportError, reportErrorCode]);

  return (
    <ControllerModal
      onClose={handleClose}
      open={open}
      title={reportResult ? 'Devices report queued' : 'Generate devices report'}
      data-modal-name='device-report'
    >
      <DialogContent>
        <Stack py={2} direction='column' spacing={3}>
          {reportResult ? (
            <>
              <Text fontSize={14}>{`The report will be emailed to ${
                getValues().email
              }. You may want to save this ID to refer back to it if you have a support request regarding this report in the future`}</Text>
              <OutlinedInput
                className='Mui-disabled'
                readOnly
                defaultValue={reportResult.uuid}
                fullWidth
                endAdornment={
                  <ClickAwayListener onClickAway={handleTooltipClose}>
                    <Tooltip title='Click to Copy' placement='top' {...additionalTooltipProps}>
                      <IconButton onClick={handleCopyToClipboard}>
                        <MdCopyAll />
                      </IconButton>
                    </Tooltip>
                  </ClickAwayListener>
                }
              />
              <Alert
                severity='success'
                variant='outlined'
              >{`Generating report ID ${reportResult.uuid}`}</Alert>
            </>
          ) : (
            <>
              <Text>We will email you a device CSV report when it&apos;s ready.</Text>
              <ControllerValidatedTextField
                name='email'
                required
                fullWidth
                autoFocus
                control={control}
                sx={{ margin: 0 }}
              />
            </>
          )}

          {reportBusy && (
            <CircularProgress
              sx={{
                alignSelf: 'center',
              }}
              color='secondary'
            />
          )}

          {reportError && (
            <>
              <Alert severity='error' variant='outlined'>
                {reportErrorCode === 429
                  ? 'We are already generating a report for you, and can only process one at a time. Please contact Mason support if you think this is a mistake'
                  : 'There was a problem generating a report. Please try again later'}
              </Alert>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {reportResult ? (
          <Button color='primary' variant='contained' onClick={handleClose} size='medium'>
            Close
          </Button>
        ) : (
          <>
            <Button color='secondary' onClick={handleClose} size='medium' variant='text'>
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={handleSubmit}
              size='medium'
              variant='contained'
              disabled={!isValid || reportBusy}
            >
              Send
            </Button>
          </>
        )}
      </DialogActions>
    </ControllerModal>
  );
}
